import { ComponentType, lazy } from "react";
import * as Sentry from "@sentry/react";
import { Severity } from "@sentry/react";
import { reloadPage } from "./reloadPage";

const MS_PER_MINUTE = 60 * 1000;

export const RELOAD_GUARD_STORAGE_KEY = "chunkLoadReloadAttemptedAt";
// Long enough that a slow reload plus sign-in cannot outlast it and turn a missing chunk into a
// reload loop.
export const RELOAD_GUARD_WINDOW_MS = 5 * MS_PER_MINUTE;

const JS_CHUNK_LOAD_ERROR_NAME = "ChunkLoadError";
const CSS_CHUNK_LOAD_FAILED_CODE = "CSS_CHUNK_LOAD_FAILED";
export const RECOVERED_AFTER_RELOAD_MESSAGE = "Recovered from a stale chunk after reload";

let isReloadPending = false;
// Once a chunk fails despite a recent reload, the reload did not help; a later page loading
// successfully must not be reported as a recovery.
let hasReloadFailedToRecover = false;

const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === JS_CHUNK_LOAD_ERROR_NAME ||
    ("code" in error && error.code === CSS_CHUNK_LOAD_FAILED_CODE));

const readLastReloadAt = (): number | null => {
  const storedValue = window.sessionStorage.getItem(RELOAD_GUARD_STORAGE_KEY);
  if (storedValue === null) {
    return null;
  }

  return Number(storedValue);
};

const isWithinGuardWindow = (reloadedAt: number): boolean =>
  Date.now() - reloadedAt < RELOAD_GUARD_WINDOW_MS;

// Unreadable storage counts as a recent reload: without a working guard a reload could loop.
const hasRecentReload = (): boolean => {
  try {
    const lastReloadAt = readLastReloadAt();

    return lastReloadAt !== null && isWithinGuardWindow(lastReloadAt);
  } catch {
    return true;
  }
};

const tryRecordReload = (): boolean => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_STORAGE_KEY, String(Date.now()));

    return true;
  } catch {
    return false;
  }
};

const readAndClearLastReloadAt = (): number | null => {
  try {
    const lastReloadAt = readLastReloadAt();
    window.sessionStorage.removeItem(RELOAD_GUARD_STORAGE_KEY);

    return lastReloadAt;
  } catch {
    return null;
  }
};

const settleReloadGuard = (): void => {
  const lastReloadAt = readAndClearLastReloadAt();
  if (lastReloadAt === null || !isWithinGuardWindow(lastReloadAt)) {
    return;
  }

  Sentry.captureMessage(RECOVERED_AFTER_RELOAD_MESSAGE, Severity.Warning);
};

// Never settles, so Suspense keeps showing the loading fallback until the browser unloads the page.
const waitForReload = (): Promise<never> => new Promise<never>(() => undefined);

const handleLoaded = <T>(module: T): T => {
  if (!isReloadPending && !hasReloadFailedToRecover) {
    settleReloadGuard();
  }

  return module;
};

const handleFailed = (error: unknown): Promise<never> => {
  if (!isChunkLoadError(error)) {
    throw error;
  }

  // Offline, a reload would swap the app shell for the browser's offline page.
  if (!window.navigator.onLine) {
    throw error;
  }

  if (isReloadPending) {
    return waitForReload();
  }

  if (hasRecentReload()) {
    hasReloadFailedToRecover = true;
    throw error;
  }

  if (!tryRecordReload()) {
    throw error;
  }

  isReloadPending = true;
  reloadPage();

  return waitForReload();
};

// A tab left open across a deploy still references the previous build's chunk hashes, which the
// new deploy no longer serves; one guarded reload picks up the current build instead of failing
// the route.
export const lazyWithReload = <T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
) => lazy(() => importer().then(handleLoaded, handleFailed));
